import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ArrowBack, CloudUpload, Download } from '@mui/icons-material';
import * as XLSX from 'xlsx';
import apiService from '../../services/api';

const EXPECTED_HEADERS = [
  'Service',
  'Regular Price',
  'Member Price',
  'Category',
  'Sub Category',
  'Gender',
];

function normalizeHeader(h) {
  return String(h || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function parsePrice(value) {
  const s = String(value ?? '').trim();
  if (!s) return null;
  const cleaned = s.replace(/[^0-9.-]/g, '');
  if (!cleaned) return null;
  const num = Number(cleaned);
  return Number.isFinite(num) ? num : null;
}

const TenantServicesUpload = () => {
  const navigate = useNavigate();
  const { tenantId } = useParams();

  const [tenant, setTenant] = useState(null);
  const [loadingTenant, setLoadingTenant] = useState(true);
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [headerError, setHeaderError] = useState('');
  const [error, setError] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  useEffect(() => {
    const loadTenant = async () => {
      try {
        setLoadingTenant(true);
        setError('');
        const res = await apiService.get(`/superadmin/tenants/${tenantId}`);
        if (res.success) setTenant(res.data);
        else setError(res.message || 'Failed to load tenant');
      } catch (e) {
        setError(e.message || 'Failed to load tenant');
      } finally {
        setLoadingTenant(false);
      }
    };

    if (tenantId) loadTenant();
  }, [tenantId]);

  const handleFileChange = async (e) => {
    const selected = e.target.files?.[0] || null;
    setFile(selected);
    setRows([]);
    setImportResult(null);
    setHeaderError('');
    setError('');

    if (!selected) return;
    if (!String(selected.name || '').toLowerCase().endsWith('.csv')) {
      setHeaderError('Please upload a .csv file');
      return;
    }

    try {
      const buffer = await selected.arrayBuffer();
      const wb = XLSX.read(buffer, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const sheetRows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
      if (!sheetRows || sheetRows.length < 2) {
        setHeaderError('CSV must include a header row and at least 1 data row');
        return;
      }

      const header = sheetRows[0];
      const normalizedHeader = header.map(normalizeHeader);
      const expected = EXPECTED_HEADERS.map(normalizeHeader);

      for (let i = 0; i < expected.length; i++) {
        if (normalizedHeader[i] !== expected[i]) {
          setHeaderError(
            `Invalid header. Expected: ${EXPECTED_HEADERS.join(', ')}`
          );
          return;
        }
      }

      const dataRows = sheetRows.slice(1).map((r, idx) => ({
        _row: idx + 2, // 1-based with header row => first data row is row 2
        service: String(r[0] ?? '').trim(),
        regular_price: parsePrice(r[1]),
        member_price: parsePrice(r[2]),
        category: String(r[3] ?? '').trim(),
        sub_category: String(r[4] ?? '').trim(),
        gender: String(r[5] ?? '').trim(),
      }));

      setRows(dataRows);
    } catch (err) {
      setHeaderError(err.message || 'Failed to parse CSV');
    }
  };

  const downloadTemplate = () => {
    const content =
      'Service,Regular Price,Member Price,Category,Sub Category,Gender\n' +
      'Hair Cut,500,400,Hair,Men,Men\n';
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'services_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = async () => {
    if (!tenantId) return;
    if (!file) {
      setError('Please choose a CSV file');
      return;
    }
    if (headerError) {
      setError('Fix CSV header issues before importing');
      return;
    }

    try {
      setImporting(true);
      setError('');
      setImportResult(null);

      const formData = new FormData();
      formData.append('file', file);
      formData.append('tenant_id', String(tenantId));

      const res = await apiService.postFormData(
        `/superadmin/tenants/${tenantId}/services/import`,
        formData
      );

      if (res.success) {
        setImportResult(res.data);
      } else {
        setError(res.message || 'Import failed');
      }
    } catch (e) {
      setError(e.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const previewRows = rows.slice(0, 5);

  if (loadingTenant) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold" color="primary.main">
            Upload Services
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Tenant: {tenant?.name || `#${tenantId}`}
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate('/superadmin/tenants')}>
          Back
        </Button>
      </Box>

      {(error || headerError) && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => { setError(''); setHeaderError(''); }}>
          {error || headerError}
        </Alert>
      )}

      {importResult && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setImportResult(null)}>
          Imported: {importResult.inserted_services ?? 0} services, {importResult.inserted_categories ?? 0} categories, {importResult.inserted_subcategories ?? 0} sub-categories.
          {importResult.skipped_rows ? ` Skipped rows: ${importResult.skipped_rows}.` : ''}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
          <Button variant="outlined" startIcon={<Download />} onClick={downloadTemplate}>
            Download CSV Template
          </Button>
          <Button
            component="label"
            variant="contained"
            startIcon={<CloudUpload />}
          >
            Choose CSV
            <input hidden type="file" accept=".csv" onChange={handleFileChange} />
          </Button>
          <Typography variant="body2" color="text.secondary">
            {file ? file.name : 'No file chosen'}
          </Typography>
          <Box flexGrow={1} />
          <Button
            variant="contained"
            onClick={handleImport}
            disabled={!file || !!headerError || importing}
          >
            {importing ? 'Importing...' : 'Import'}
          </Button>
        </Box>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          Required columns (header row): {EXPECTED_HEADERS.join(', ')}
        </Typography>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
          Preview (first 5 rows)
        </Typography>
        {rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Upload a CSV to see a preview.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Row</TableCell>
                <TableCell>Service</TableCell>
                <TableCell>Regular Price</TableCell>
                <TableCell>Member Price</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Sub Category</TableCell>
                <TableCell>Gender</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {previewRows.map((r) => (
                <TableRow key={r._row}>
                  <TableCell>{r._row}</TableCell>
                  <TableCell>{r.service}</TableCell>
                  <TableCell>{r.regular_price ?? ''}</TableCell>
                  <TableCell>{r.member_price ?? ''}</TableCell>
                  <TableCell>{r.category}</TableCell>
                  <TableCell>{r.sub_category}</TableCell>
                  <TableCell>{r.gender}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      {importResult?.row_errors?.length > 0 && (
        <Paper sx={{ p: 2, mt: 3 }}>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
            Row Errors ({importResult.row_errors.length})
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            These rows were skipped during import. Fix them in the CSV and re-upload.
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>CSV Row</TableCell>
                  <TableCell>Error</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {importResult.row_errors.map((e, idx) => (
                  <TableRow key={`${e.row}-${idx}`}>
                    <TableCell>{e.row}</TableCell>
                    <TableCell>{e.error}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
};

export default TenantServicesUpload;


